/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable prettier/prettier */
/* eslint-disable @next/next/no-img-element */
import _useTranslation from 'lib/hooks/_useTranslation'
import { Body1, Body2 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import { useEffect, useMemo, useState } from 'react'
import Stack from 'components/elements/Stack'
import { Know } from 'interfaces/Know'
import NewButton from 'components/elements/NewButton'
import { trackEvent } from 'lib/tracking/analytics-service'
import {
    AnalyticsEventPractisingElementAnswer,
    AnalyticsEventPractisingElementImpression,
} from 'lib/constants/AnalyticsEventType'
import { PractiseSet } from 'interfaces/PractiseSet'

const Container = styled.div<{
    isAnswerCorrect?: boolean
    isAnswerWrong?: boolean
}>`
    background: linear-gradient(159.54deg, rgba(11, 9, 37, 0.2) 0%, rgba(210, 33, 90, 0.2) 100%);
    padding: 24px 10%;
    display: flex;
    flex-direction: column;
    justify-content: start;
    transition: height 0.5s ease-in-out, opacity 0.5s ease-in-out;
    width: 100%;
    min-height: 500px;
    border-radius: ${theme.borderRadius.rounded};
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: ${theme.borderRadius.rounded};
        padding: 4px;

        background: ${({ isAnswerCorrect, isAnswerWrong }) => {
            if (isAnswerCorrect) {
                return `conic-gradient(
                    from 180.15deg at 50% 50%,
                    #2A642C 0deg,
                    #047E72 90.67deg,
                    #439746 178.2deg,
                    #047E72 271.17deg,
                    #2A642C 360deg
                )`
            }
            if (isAnswerWrong) {
                return `conic-gradient(
                    from 180.15deg at 50% 50%,
                    #D12B25 0deg,
                    #B35200 90.67deg,
                    #D12B25 180.16deg,
                    #D66300 271.17deg,
                    #D12B25 360deg
                )`
            }
            return `conic-gradient(
                from 180.15deg at 50% 50%,
                #6c3ebc 0deg,
                #8bbdff 90.67deg,
                #797fdd 180.02deg,
                #8bbdff 271.17deg,
                #6c3ebc 360deg
            )`
        }};
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask-composite: xor;
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        pointer-events: none;
    }

    @media (max-width: 1050px) {
        height: fit-content !important;
    }
    gap: 24px;

    @media (max-width: 800px) {
        gap: 10px;
        padding: 25px;
    }

    .steps {
        width: 100%;
        height: 16px;
        background: ${theme.colors.grayScaleSecondaryTransparent};
        color: #ffffff26;
        border-radius: 12px;
        position: relative;
        overflow: hidden;
        p {
            position: absolute;
            top: 10px;
        }
    }

    .question {
        width: 100%;
        border-radius: ${theme.borderRadius.normal};
        background-color: ${theme.colors.transparent};
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2;
        transition: all 0.3s ease-in-out;
        p {
            font-family: var(--font-inter);
            font-size: 23px;
            line-height: 28px;
            font-weight: 650;
            color: ${theme.colors.white};
            letter-spacing: -0.2px;
        }
    }

    .explanation {
        width: 100%;
        border-radius: ${theme.borderRadius.normal};
        background-color: rgba(255, 255, 255, 0.1);
        padding: 16px;
        margin-top: 16px;
        z-index: 2;
        p {
            font-family: var(--font-inter);
            font-size: 16px;
            line-height: 20px;
            font-weight: 400;
            color: ${theme.colors.white};
            letter-spacing: 0.1px;
        }
    }

    .answers {
        width: 100%;
        gap: 12px;
        display: grid;
        justify-content: center;
        grid-template-columns: 1fr;
        transition: all 0.3s ease-in-out;

        @media (max-width: 750px) {
            grid-template-columns: 1fr;
        }
    }

    div {
        z-index: 2;
    }
    position: relative;
`

const Card = styled.div<{ isCorrect?: boolean; isWrong?: boolean; isQuestionShown?: boolean }>`
    width: 100%;
    border-radius: 12px;
    background: linear-gradient(102.61deg, rgba(87, 103, 214, 0.2) 1.56%, rgba(113, 19, 129, 0.2) 97.23%);

    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    z-index: 2;

    position: relative;
    border: 2px solid ${theme.colors.backgroundTransparent};
    transition: border 0.2s ease-in-out, opacity 0.2s ease-in-out;
    p {
        font-family: var(--font-inter);
        font-size: 17px;
        font-weight: 450;
        line-height: 24px;
        letter-spacing: 0.3px;
        color: ${theme.colors.white};
    }

    ${({ isQuestionShown }) =>
        isQuestionShown
            ? css`
                  cursor: pointer;
                  :hover {
                      border: 2px solid rgba(0, 141, 232, 1);
                      background: rgba(5, 49, 84, 1);
                  }
              `
            : ''}
    ${({ isCorrect }) =>
        isCorrect
            ? css`
                  cursor: auto;
                  border: 2px solid rgba(67, 151, 70, 1);
                  background: rgba(67, 151, 70, 1);
              `
            : ''}
    ${({ isWrong }) =>
        isWrong
            ? css`
                  cursor: auto;
                  border: 2px solid rgba(209, 43, 37, 1);
                  background: rgba(209, 43, 37, 1);
              `
            : ''}
`

const Step = styled.div<{ progressPercentage: number }>`
    width: ${({ progressPercentage }) => `${progressPercentage}%`};
    height: 16px;
    border-radius: 12px;
    background: linear-gradient(270deg, #9a76cf 0%, #3f51b5 100%),
        radial-gradient(50% 50% at 50% 0%, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 100%);
    background-blend-mode: normal, overlay;
    transition: width 0.3s ease-in-out;
`

enum PractisingElementStatus {
    Question = 'QUESTION',
    CorrectAnswer = 'CORRECT_ANSWER',
    WrongAnswer = 'WRONG_ANSWER',
}

interface Props {
    know: Know
    practiseSet: PractiseSet
    isDarkTheme?: boolean
}

const PractiseSetQuizPreview = ({ know, practiseSet, isDarkTheme }: Props) => {
    const [progressStatus, setProgressStatus] = useState<PractisingElementStatus>(PractisingElementStatus.Question)
    const [selectedWrongAnswer, setSelectedWrongAnswer] = useState('')
    const [questionIndex, setQuestionIndex] = useState(0)
    const [answeredQuestions, setAnsweredQuestions] = useState(0)
    const { t } = _useTranslation('knows')

    const isQuestionShown = progressStatus === PractisingElementStatus.Question
    const questions = practiseSet.flashcards

    useEffect(() => {
        if (!know) return
        trackEvent(AnalyticsEventPractisingElementImpression, { know })
    }, [know])

    const onAnswerClicked = (isCorrect: boolean, answer: string) => {
        if (!isQuestionShown) return

        trackEvent(AnalyticsEventPractisingElementAnswer, {
            know,
            value: isCorrect ? 'correct' : 'wrong',
            valueId: questionIndex,
        })

        setProgressStatus(isCorrect ? PractisingElementStatus.CorrectAnswer : PractisingElementStatus.WrongAnswer)
        setAnsweredQuestions((prev) => prev + 1)

        if (!isCorrect) {
            setSelectedWrongAnswer(answer)
        }
    }

    const loadNextQuestion = () => {
        if (questionIndex === questions.length - 1) {
            // Reset to first question
            setQuestionIndex(0)
            setAnsweredQuestions(0)
        } else {
            setQuestionIndex((index) => ++index)
        }
        setProgressStatus(PractisingElementStatus.Question)
        setSelectedWrongAnswer('')
    }

    const selectedQuestion = questions[questionIndex]
    const wasAnswerGiven = progressStatus !== PractisingElementStatus.Question

    const answers = useMemo(() => {
        if (selectedQuestion?.type === 'TRUE_FALSE') {
            return [
                { isCorrect: selectedQuestion.isCorrect, answer: 'True' },
                { isCorrect: !selectedQuestion.isCorrect, answer: 'False' },
            ]
        } else if (selectedQuestion?.type === 'MULTIPLE_CHOICE' && selectedQuestion.answerOptions) {
            return selectedQuestion.answerOptions.map((option) => ({
                isCorrect: option.isCorrect,
                answer: option.answerText,
            }))
        }
        return []
    }, [selectedQuestion])

    const getExplanation = () => {
        if (progressStatus === PractisingElementStatus.CorrectAnswer) {
            return selectedQuestion?.correctAnswerExplanation
        } else if (progressStatus === PractisingElementStatus.WrongAnswer) {
            return selectedQuestion?.incorrectAnswerExplanation
        }
        return null
    }

    return (
        <Container
            isAnswerCorrect={progressStatus === PractisingElementStatus.CorrectAnswer}
            isAnswerWrong={progressStatus === PractisingElementStatus.WrongAnswer}
        >
            <Stack gutter={24}>
                <div className="steps">
                    <Step progressPercentage={(answeredQuestions / questions.length) * 100} />
                </div>

                <Stack gutter={24}>
                    <div className="question">
                        <Body1 fontFamily="var(--font-inter)" fontWeight={500} textAlign="center">
                            {selectedQuestion?.questionText}
                        </Body1>
                    </div>

                    <div className="answers">
                        {answers.map(({ answer, isCorrect }, i) => (
                            <Card
                                key={`${answer}-${i}`}
                                onClick={() => {
                                    if (!isQuestionShown) return
                                    onAnswerClicked(isCorrect, answer)
                                }}
                                isQuestionShown={isQuestionShown}
                                isWrong={answer === selectedWrongAnswer}
                                isCorrect={isCorrect && !isQuestionShown}
                            >
                                <Body1 fontFamily="var(--font-inter)" fontWeight={500} textAlign="center" fontSize={14}>
                                    {answer}
                                </Body1>
                            </Card>
                        ))}
                    </div>

                    {wasAnswerGiven && getExplanation() && (
                        <div className="explanation">
                            <Body2 fontFamily="var(--font-inter)" fontWeight={400} textAlign="center">
                                {getExplanation()}
                            </Body2>
                        </div>
                    )}
                </Stack>
            </Stack>

            {wasAnswerGiven ? (
                <NewButton
                    onClick={loadNextQuestion}
                    color={theme.colors.deepSeaBlue}
                    width={300}
                    fullWidth
                    height={50}
                    borderRadius={theme.borderRadius.card}
                    isWebapp
                >
                    <Body2 fontSize={17} fontWeight={500} fontFamily="var(--font-inter)" color={theme.colors.white}>
                        {questionIndex === questions.length - 1
                            ? t('knows/practisingElementsRestartQuizButtonLabel', 'Restart')
                            : t('knows/practisingElementsNextQuestionButtonLabel')}
                    </Body2>
                </NewButton>
            ) : (
                <div style={{ height: 50 }} />
            )}
        </Container>
    )
}

export default PractiseSetQuizPreview
